import { Link } from "react-router-dom";
import {
  AlertOctagon, CalendarDays, CheckCircle2, Clock, ClipboardList,
  FileWarning, StickyNote, Wrench,
} from "lucide-react";
import { fmtDate, fmtCurrency } from "../../lib/status";
import { Info } from "./vehicleUtils";

export function VehicleOverviewTab({ v, summary, onTabChange }) {
  return (
    <div className="space-y-4" data-testid="vd-overview">
      {summary && (
        <SummaryCards summary={summary} vehicleId={v.id} onTabChange={onTabChange} />
      )}
      <div className="rounded-xl border border-slate-200 bg-white p-6 grid sm:grid-cols-4 gap-4 text-sm">
        <Info label="Category" value={v.category} />
        <Info label="Transmission" value={v.transmission} />
        <Info label="Seats" value={v.seats} />
        <Info label="Price/day" value={fmtCurrency(v.price_per_day)} />
        {v.notes && (
          <div className="sm:col-span-4">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Notes</div>
            <p className="mt-1 whitespace-pre-line">{v.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCards({ summary, vehicleId, onTabChange }) {
  const b = summary.bookings || {};
  const dam = summary.damages || {};
  const inc = summary.incidents || {};
  const maint = summary.maintenance || {};
  const docs = summary.documents || {};
  const ins = summary.inspections || {};
  const notes = summary.notes || {};

  const cards = [];

  // Current / next booking
  if (b.current) {
    cards.push({
      key: "current-booking",
      tone: "emerald",
      icon: CalendarDays,
      label: "Currently rented",
      value: b.current.customer_name,
      sub: `${fmtDate(b.current.start_datetime)} → ${fmtDate(b.current.end_datetime)}`,
      link: `/app/bookings/${b.current.id}`,
    });
  } else if (b.upcoming_next) {
    cards.push({
      key: "next-booking",
      tone: "blue",
      icon: Clock,
      label: `Next booking · ${b.upcoming_count} upcoming`,
      value: b.upcoming_next.customer_name,
      sub: fmtDate(b.upcoming_next.start_datetime),
      link: `/app/bookings/${b.upcoming_next.id}`,
    });
  } else {
    cards.push({
      key: "no-booking",
      tone: "slate",
      icon: CheckCircle2,
      label: "Bookings",
      value: "Available",
      sub: `${b.completed_count || 0} completed total`,
      onClick: () => onTabChange("bookings"),
    });
  }

  // Open damages
  cards.push({
    key: "open-damages",
    tone: dam.open > 0 ? "amber" : "slate",
    icon: AlertOctagon,
    label: "Open damages",
    value: String(dam.open || 0),
    sub: `${dam.total || 0} total recorded`,
    link: `/app/vehicles/${vehicleId}/damages`,
  });

  // Open incidents
  cards.push({
    key: "open-incidents",
    tone: inc.open > 0 ? "rose" : "slate",
    icon: AlertOctagon,
    label: "Open incidents",
    value: String(inc.open || 0),
    sub: inc.open > 0 ? "needs attention" : "All clear",
    onClick: () => onTabChange("activity"),
  });

  // Maintenance
  const nd = maint.next_due;
  const maintTone = (nd && nd.next_due_state === "overdue") ? "rose"
                    : (nd && nd.next_due_state === "soon") ? "amber"
                    : maint.pending > 0 ? "blue" : "slate";
  cards.push({
    key: "maintenance",
    tone: maintTone,
    icon: Wrench,
    label: "Maintenance",
    value: `${maint.pending || 0} pending`,
    sub: nd
      ? (nd.next_due_state === "overdue"
          ? `Overdue: ${nd.title}`
          : nd.next_due_state === "soon"
            ? `Due in ${nd.next_due_days}d: ${nd.title}`
            : `Next: ${nd.title}`)
      : "No upcoming service",
    onClick: () => onTabChange("maintenance"),
  });

  // Documents expiry
  const docTone = docs.expired > 0 ? "rose" : docs.expiring_soon > 0 ? "amber" : "slate";
  cards.push({
    key: "document-expiry",
    tone: docTone,
    icon: FileWarning,
    label: "Document expiry",
    value: docs.expired > 0
      ? `${docs.expired} expired`
      : docs.expiring_soon > 0
        ? `${docs.expiring_soon} expiring`
        : "All current",
    sub: `${docs.total_with_expiry || 0} doc(s) with expiry`,
    onClick: () => onTabChange("documents"),
  });

  // Inspections
  cards.push({
    key: "inspections",
    tone: "slate",
    icon: ClipboardList,
    label: "Inspections",
    value: String(ins.total || 0),
    sub: ins.last
      ? `Last: ${(ins.last.kind || ins.last.type || "inspection").replace(/_/g, " ")} · ${ins.last.status || "draft"}`
      : "None recorded",
    onClick: () => onTabChange("inspections"),
  });

  // Internal notes
  if ((notes.total || 0) > 0) {
    cards.push({
      key: "notes",
      tone: notes.pinned > 0 ? "amber" : "slate",
      icon: StickyNote,
      label: "Internal notes",
      value: String(notes.total),
      sub: notes.pinned > 0 ? `${notes.pinned} pinned` : "Staff-only",
      onClick: () => onTabChange("notes"),
    });
  }

  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="vd-summary-cards">
      {cards.map(({ key, ...card }) => <SummaryCard key={key} cardKey={key} {...card} />)}
    </div>
  );
}

function SummaryCard({ tone, icon: Icon, label, value, sub, onClick, link, cardKey }) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50/70 text-emerald-900",
    rose:    "border-rose-200 bg-rose-50/70 text-rose-900",
    amber:   "border-amber-200 bg-amber-50/70 text-amber-900",
    blue:    "border-sky-200 bg-sky-50/70 text-sky-900",
    slate:   "border-slate-200 bg-white text-slate-800",
  };
  const iconTones = {
    emerald: "text-emerald-700",
    rose: "text-rose-700",
    amber: "text-amber-700",
    blue: "text-sky-700",
    slate: "text-slate-500",
  };
  const inner = (
    <div className={`rounded-xl border p-3 hover:shadow-sm transition ${tones[tone] || tones.slate}`}>
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${iconTones[tone] || iconTones.slate}`} />
        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold opacity-80">{label}</div>
      </div>
      <div className="mt-1 font-display text-xl font-bold truncate" title={value}>{value}</div>
      {sub && <div className="text-[11px] opacity-75 mt-0.5 line-clamp-2">{sub}</div>}
    </div>
  );
  const testid = `vd-summary-card-${cardKey}`;
  if (link) {
    return <Link to={link} className="block" data-testid={testid}>{inner}</Link>;
  }
  return (
    <button type="button" onClick={onClick} className="block w-full text-left" data-testid={testid}>
      {inner}
    </button>
  );
}
